import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MONTHS } from '@edtech-ui/core/constants/month-config.constants';
import { ResumeStepValidatable } from '@edtech-ui/core/models/resume-builder/step-validators';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { companyNameValidator, roleTitleValidator } from '@edtech-ui/core/utils/resume-validation/resume-validation-experience';
import { locationValidator } from '@edtech-ui/core/utils/resume-validation/resume-validation-personal';

@Component({
	selector: 'app-resumer-builder-experience',
	standalone: true,
	imports: [ReactiveFormsModule, FormsModule, CommonModule],
	templateUrl: './resumer-builder-experience.component.html',
	styleUrl: './resumer-builder-experience.component.scss'
})
export class ResumerBuilderExperienceComponent implements OnInit, ResumeStepValidatable {

	showForm: boolean = false;
	userProfileForm: FormGroup;
	years: number[] = [];
	months = MONTHS;
	hasExperienceData: boolean = false;

	constructor(
		private resumeService: ResumeService,
		private route: ActivatedRoute

	) {
		const currentYear = new Date().getFullYear();
		for (let year = 1980; year <= currentYear; year++) {
			this.years.push(year);
		}

		this.userProfileForm = new FormGroup({
			experience: new FormArray([this.createExperienceGroup()])
		});
	}


	ngOnInit(): void {
		const resumeId = this.route.snapshot.paramMap.get('resumeId');
		if (resumeId) {
			this.getExperienceFromApi();
		} else {
			this.getExperienceDataFromService();
		}
	}


	createExperienceGroup(): FormGroup {
		return new FormGroup({
			company: new FormControl(''),
			location: new FormControl('', [locationValidator]),
			role: new FormControl(''),
			startMonth: new FormControl(''),
			startYear: new FormControl(''),
			endMonth: new FormControl(''),
			endYear: new FormControl(''),
			currently_working: new FormControl(false),
			description: new FormControl('')
		},
			{ validators: this.dateRangeValidator.bind(this) }
		);
	}


	get experience(): FormArray {
		return this.userProfileForm.get('experience') as FormArray;
	}


	addExperience(): void {
		this.experience.push(this.createExperienceGroup());
	}


	removeExperience(index: number): void {
		if (this.experience.length > 1) {
			this.experience.removeAt(index);
		}
	}


	isCurrentlyWorking(index: number): boolean {
		return this.experience.at(index).get('currently_working')?.value;
	}


	onCurrentlyWorkingChange(event: any, index: number) {
		const exp = this.experience.at(index);

		if (event.target.checked) {
			exp.patchValue({
				currently_working: true,
				endMonth: 'Present',
				endYear: ''
			});
		} else {
			exp.patchValue({
				currently_working: false,
				endMonth: '',
				endYear: ''
			});
		}
		exp.updateValueAndValidity();
	}


	closeForm() {
		this.showForm = false;
		this.userProfileForm.reset();
		this.userProfileForm.setControl('experience', new FormArray([this.createExperienceGroup()]));
	}


	saveData(): void {
		if (this.userProfileForm.invalid) return;

		const payload = (this.userProfileForm.value.experience || []).map((exp: any) => ({
			company: exp.company,
			location: exp.location,
			role: exp.role,
			startMonth: exp.startMonth,
			startYear: exp.startYear?.toString() || '',
			endMonth: exp.currently_working ? 'Present' : exp.endMonth,
			endYear: exp.currently_working ? '' : exp.endYear?.toString() || '',
			currently_working: exp.currently_working,
			description: exp.description
		}));
		this.resumeService.setSectionData('experience', payload);
	}


	editExperienceData(data: any[]) {
		if (!data || !data.length) return;
		this.experience.clear();
		data.forEach((exp: any) => {
			const group = this.createExperienceGroup();
			group.patchValue({
				company: exp.company || '',
				location: exp.location || '',
				role: exp.role || '',
				startMonth: exp.startMonth || '',
				startYear: exp.startYear ? Number(exp.startYear) : '',
				endMonth: exp.endMonth || '',
				endYear: exp.endYear ? Number(exp.endYear) : '',
				currently_working: exp.currently_working || false,
				description: exp.description || ''
			});
			this.experience.push(group);
		});
	}

	isFormValid(): boolean {
		return this.userProfileForm.valid;
	}


	getControl(index: number, field: string) {
		return this.experience.at(index).get(field);
	}


	getExperienceFromApi(): void {
		this.resumeService.getFullResumeData().subscribe(data => {
			if (data?.experience) {
				this.hasExperienceData = true;
				this.editExperienceData(data.experience);
				this.showForm = true;
			}
		});
	}

	getExperienceDataFromService(): void {
		const data = this.resumeService.getSectionData('experience');
		if (data && data.length) {
			this.hasExperienceData = true;
			this.editExperienceData(data);
			this.showForm = true;
		}
	}


	dateRangeValidator(control: any) {
		const group = control as FormGroup;

		const startMonth = group.get('startMonth')?.value;
		const startYear = group.get('startYear')?.value;
		const endMonth = group.get('endMonth')?.value;
		const endYear = group.get('endYear')?.value;
		const isCurrent = group.get('currently_working')?.value;

		if (isCurrent) return null;

		if (!startMonth || !startYear || !endMonth || !endYear) return null;

		const monthIndex = (m: string) => this.months.indexOf(m);

		const start = new Date(+startYear, monthIndex(startMonth));
		const end = new Date(+endYear, monthIndex(endMonth));

		return end < start ? { invalidDateRange: true } : null;
	}


}